import * as React from 'react';
import {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import {alpha, useTheme} from '@mui/material/styles';
import {useLocation, useNavigate} from 'react-router-dom';
import type {Menu} from 'src/pages/Layout/menu';
import {isNavPathActive} from './navUtils';

const ICON_BOX = 34;
const NAV_RADIUS = 2;

type Props = {
  menu: Menu;
  open: boolean;
  depth: number;
  itemRef?: React.Ref<HTMLDivElement>;
};

export function SidebarNavItem({menu, open, depth, itemRef}: Props) {
  const theme = useTheme();
  const {title, icon, path, children} = menu;
  const location = useLocation();
  const navigate = useNavigate();
  const [subOpen, setSubOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const menuItemRef = React.useRef<HTMLDivElement | null>(null) as React.MutableRefObject<HTMLDivElement | null>;

  const setItemRef = (node: HTMLDivElement | null) => {
    menuItemRef.current = node;
    if (!itemRef) return;
    if (typeof itemRef === 'function') {
      itemRef(node);
    } else {
      (itemRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  };
  const [mouseLeaveTimer, setMouseLeaveTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const hasChildren = Boolean(children?.length);
  const isActive = isNavPathActive(path, location.pathname);
  const isChildActive =
    hasChildren && children!.some(c => isNavPathActive(c.path, location.pathname));

  useEffect(() => {
    if (open && hasChildren && isChildActive) {
      setSubOpen(true);
    }
  }, [open, hasChildren, isChildActive, location.pathname]);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!open && hasChildren) {
      if (mouseLeaveTimer) {
        clearTimeout(mouseLeaveTimer);
        setMouseLeaveTimer(null);
      }
      setAnchorEl(event.currentTarget);
      setShowPopup(true);
    }
  };

  const handleMouseLeave = () => {
    if (!open && hasChildren) {
      const timer = setTimeout(() => setShowPopup(false), 800);
      setMouseLeaveTimer(timer);
    }
  };

  useEffect(() => {
    return () => {
      if (mouseLeaveTimer) clearTimeout(mouseLeaveTimer);
    };
  }, [mouseLeaveTimer]);

  const handleClickAway = () => {
    setShowPopup(false);
    if (mouseLeaveTimer) {
      clearTimeout(mouseLeaveTimer);
      setMouseLeaveTimer(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showPopup &&
        menuItemRef.current &&
        !menuItemRef.current.contains(event.target as Node) &&
        !document.querySelector('.menu-popup-paper')?.contains(event.target as Node)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopup]);

  const active = isActive || (hasChildren && isChildActive);

  const buttonSx = {
    minHeight: depth > 0 ? 40 : 42,
    justifyContent: open ? 'flex-start' : 'center',
    px: open ? 1.25 : 1,
    py: 0.75,
    borderRadius: NAV_RADIUS,
    position: 'relative' as const,
    overflow: 'hidden',
    ml: depth > 0 && open ? 1.5 : 0,
    ...(active && {
      bgcolor: alpha(theme.palette.primary.main, 0.09),
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: '0 4px 4px 0',
        bgcolor: 'primary.main',
      },
    }),
    ...(!active && {
      '&:hover': {
        bgcolor: alpha(theme.palette.text.primary, 0.04),
      },
    }),
  };

  const iconWrapSx = {
    width: ICON_BOX,
    height: ICON_BOX,
    minWidth: ICON_BOX,
    borderRadius: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mr: open ? 1.25 : 0,
    transition: theme.transitions.create(['background-color', 'box-shadow'], {duration: 180}),
    ...(active
      ? {
          bgcolor: alpha(theme.palette.primary.main, 0.14),
          color: 'primary.main',
          '& .MuiSvgIcon-root': {fontSize: 20, color: 'primary.main'},
        }
      : {
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          color: 'text.secondary',
          '& .MuiSvgIcon-root': {fontSize: 20},
        }),
  };

  const primaryTypography = {
    fontWeight: active ? 600 : 500,
    fontSize: '0.875rem',
    letterSpacing: '-0.01em',
    color: active ? 'text.primary' : 'text.secondary',
  };

  return (
    <>
      <Box
        ref={setItemRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{width: '100%', py: 0}}
      >
        <ListItemButton
          selected={false}
          sx={buttonSx}
          onClick={() => {
            if (hasChildren) {
              if (open) {
                setSubOpen(!subOpen);
              } else {
                setShowPopup(!showPopup);
              }
            } else {
              navigate(path);
            }
          }}
        >
          <Tooltip title={!open ? title : ''} placement="right" disableHoverListener={open || hasChildren}>
            <ListItemIcon sx={{minWidth: 0, mr: 0, justifyContent: 'center'}}>
              <Box sx={iconWrapSx}>{icon}</Box>
            </ListItemIcon>
          </Tooltip>
          <ListItemText
            primary={title}
            sx={{
              opacity: open ? 1 : 0,
              m: 0,
              '& .MuiListItemText-primary': primaryTypography,
            }}
          />
          {hasChildren && open && (
            <Box sx={{display: 'flex', color: 'text.secondary', ml: 'auto'}}>
              {subOpen ? (
                <ExpandMoreOutlinedIcon sx={{fontSize: 20}} />
              ) : (
                <ChevronRightIcon sx={{fontSize: 20}} />
              )}
            </Box>
          )}
        </ListItemButton>
      </Box>

      {open && subOpen && hasChildren && (
        <Box sx={{pl: depth > 0 ? 1.5 : 0, mt: 0.25, display: 'flex', flexDirection: 'column', gap: 0.25}}>
          {children!.map(child => (
            <SidebarNavItem key={child.title} menu={child} open={open} depth={depth + 1} />
          ))}
        </Box>
      )}

      {!open && hasChildren && (
        <Popper
          open={showPopup}
          anchorEl={anchorEl}
          placement="right-start"
          transition
          style={{zIndex: 9999, marginLeft: 8}}
          modifiers={[
            {name: 'offset', options: {offset: [0, -5]}},
            {name: 'preventOverflow', options: {boundary: document.body}},
          ]}
        >
          {({TransitionProps}) => (
            <Grow {...TransitionProps} style={{transformOrigin: 'left top'}}>
              <Paper
                className="menu-popup-paper"
                elevation={8}
                onMouseEnter={() => {
                  if (mouseLeaveTimer) {
                    clearTimeout(mouseLeaveTimer);
                    setMouseLeaveTimer(null);
                  }
                  setShowPopup(true);
                }}
                onMouseLeave={() => {
                  const timer = setTimeout(() => setShowPopup(false), 300);
                  setMouseLeaveTimer(timer);
                }}
                sx={{
                  width: 220,
                  overflow: 'visible',
                  borderRadius: 2,
                  boxShadow: '0 8px 28px rgba(15, 23, 42, 0.12)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                }}
              >
                <ClickAwayListener onClickAway={handleClickAway}>
                  <List disablePadding>
                    {children!.map(childMenu => (
                      <ListItem key={childMenu.title} disablePadding sx={{display: 'block'}}>
                        <ListItemButton
                          selected={isNavPathActive(childMenu.path, location.pathname)}
                          onClick={() => {
                            navigate(childMenu.path);
                            setShowPopup(false);
                          }}
                          sx={{
                            py: 1.25,
                            px: 1.5,
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              borderLeft: `3px solid ${theme.palette.primary.main}`,
                              pl: 1.25,
                            },
                            '&:hover': {bgcolor: alpha(theme.palette.text.primary, 0.04)},
                          }}
                        >
                          {childMenu.icon && (
                            <ListItemIcon sx={{minWidth: 36}}>{childMenu.icon}</ListItemIcon>
                          )}
                          <ListItemText
                            primary={childMenu.title}
                            primaryTypographyProps={{fontWeight: 600, fontSize: '0.875rem'}}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
}
